import { useState } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { Brain, CalendarDays, MessageSquareText } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { pickDefaultProvider } from '@/lib/analyticsDefaultProvider'
import { Permissions } from '@/lib/permissions'
import {
  useTokenAnalyticsByModel,
  useTokenAnalyticsByUser,
  useTokenAnalyticsByWorkflow,
} from '@/hooks/useAnalytics'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TokenUsageTable, type TokenUsageTableRow } from '@/components/analytics/TokenUsageTable'
import { UsageSummaryCards } from '@/components/analytics/UsageSummaryCards'
import { EditSquareIcon, ProjectIcon, TextCompareIcon, WorkflowDefinitionOutlineIcon } from '@/components/icons'
import { ProjectSectionHeader } from '@/components/projects'
import type { LinkedEntityScope } from '@/types'
import type { AiProvider } from '@/types/analytics'

const SCOPE_ICON_MAP: Record<LinkedEntityScope, React.ComponentType<{ className?: string }>> = {
  WorkItem: TextCompareIcon,
  Intent: EditSquareIcon,
  Project: ProjectIcon,
}

function WorkflowScopeIcon({ scope }: { scope: string | null }) {
  const Icon = scope && scope in SCOPE_ICON_MAP
    ? SCOPE_ICON_MAP[scope as LinkedEntityScope]
    : WorkflowDefinitionOutlineIcon
  return <Icon className="h-4 w-4 text-accent-soft shrink-0" />
}

const PROVIDER_OPTIONS: { value: AiProvider; label: string }[] = [
  { value: 'Anthropic', label: 'Anthropic' },
  { value: 'GitHub', label: 'GitHub Copilot' },
  { value: 'Titan', label: 'Qubiz Titan' },
]

type Period = 'this-month' | 'last-month' | 'custom'
type View = 'by-workflow' | 'by-user' | 'by-model'

const VIEW_OPTIONS: { value: View; label: string }[] = [
  { value: 'by-workflow', label: 'By Workflow' },
  { value: 'by-user', label: 'By User' },
  { value: 'by-model', label: 'By Model' },
]

function formatLocalDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getDateRange(period: Period, customFrom: string, customTo: string) {
  const now = new Date()

  if (period === 'this-month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1)
    return {
      from: formatLocalDate(from),
      to: formatLocalDate(now),
    }
  }

  if (period === 'last-month') {
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1)
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0)
    return {
      from: formatLocalDate(firstDay),
      to: formatLocalDate(lastDay),
    }
  }

  return {
    from: customFrom || undefined,
    to: customTo || undefined,
  }
}

export function AnalyticsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const canViewAnalytics = hasPermission(Permissions.Project.Analytics.Read, projectId)

  const [selectedProvider, setSelectedProvider] = useState<AiProvider | null>(null)
  const [period, setPeriod] = useState<Period>('this-month')
  const [view, setView] = useState<View>('by-workflow')
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')

  const thisMonth = getDateRange('this-month', '', '')
  const noProviderChosen = selectedProvider === null
  const detectingDefault = canViewAnalytics && noProviderChosen
  const anthropicUsage = useTokenAnalyticsByModel(projectId, { provider: 'Anthropic', ...thisMonth }, { enabled: detectingDefault })
  const githubUsage = useTokenAnalyticsByModel(projectId, { provider: 'GitHub', ...thisMonth }, { enabled: detectingDefault })
  const titanUsage = useTokenAnalyticsByModel(projectId, { provider: 'Titan', ...thisMonth }, { enabled: detectingDefault })

  const defaultProviderSettled = !anthropicUsage.isPending && !githubUsage.isPending && !titanUsage.isPending
  const providerResolved = !noProviderChosen || defaultProviderSettled
  const provider: AiProvider = selectedProvider ?? (defaultProviderSettled
    ? pickDefaultProvider({ Anthropic: anthropicUsage.data, GitHub: githubUsage.data, Titan: titanUsage.data })
    : 'Anthropic')

  const queryParams = { provider, ...getDateRange(period, customFrom, customTo) }

  const viewEnabled = canViewAnalytics && providerResolved
  const workflowQuery = useTokenAnalyticsByWorkflow(projectId, queryParams, { enabled: viewEnabled && view === 'by-workflow' })
  const userQuery = useTokenAnalyticsByUser(projectId, queryParams, { enabled: viewEnabled && view === 'by-user' })
  const modelQuery = useTokenAnalyticsByModel(projectId, queryParams, { enabled: viewEnabled && view === 'by-model' })

  if (authLoading) return null
  if (!canViewAnalytics) return <Navigate to={`/projects/${projectId}/overview`} replace />

  const workflowRows: TokenUsageTableRow[] | undefined = workflowQuery.data?.rows.map(row => ({
    key: row.workflowDefinitionId ?? `${row.sourceCategory}-${row.displayName}`,
    label: (
      <div className="flex items-center gap-3">
        {row.sourceCategory === 'chat' ? (
          <MessageSquareText className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : row.sourceCategory === 'agent' ? (
          <Brain className="h-4 w-4 text-muted-foreground shrink-0" />
        ) : (
          <WorkflowScopeIcon scope={row.linkedEntityScope} />
        )}
        <span className="min-w-0 text-sm">{row.displayName}</span>
      </div>
    ),
    inputTokens: row.inputTokens,
    outputTokens: row.outputTokens,
    cachedTokens: row.cachedTokens,
    totalTokens: row.totalTokens,
    costUsd: row.costUsd,
  }))

  const userRows: TokenUsageTableRow[] | undefined = userQuery.data?.users.map(user => ({
    key: user.userId,
    label: (
      <div className="flex items-center gap-3">
        <Avatar className="h-9 w-9 shrink-0">
          <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
            {user.initials}
          </AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col" data-testid="stacked-identity">
          <span className="text-sm font-medium leading-tight">{user.displayName}</span>
          <span className="text-xs text-muted-foreground">{user.email}</span>
        </div>
      </div>
    ),
    inputTokens: user.inputTokens,
    outputTokens: user.outputTokens,
    cachedTokens: user.cachedTokens,
    totalTokens: user.totalTokens,
    costUsd: user.costUsd,
  }))

  const modelRows: TokenUsageTableRow[] | undefined = modelQuery.data?.rows.map(row => ({
    key: row.model,
    label: <span className="min-w-0 text-sm">{row.model}</span>,
    inputTokens: row.inputTokens,
    outputTokens: row.outputTokens,
    cachedTokens: row.cachedTokens,
    totalTokens: row.totalTokens,
    costUsd: row.costUsd,
  }))

  const summary = view === 'by-workflow' ? workflowQuery.data : view === 'by-model' ? modelQuery.data : undefined

  return (
    <div>
      <ProjectSectionHeader sectionName="Analytics" />

      <div className="flex items-center gap-3 mt-5 mb-6">
        <div className="w-48">
          <Select value={provider} onValueChange={v => setSelectedProvider(v as AiProvider)}>
            <SelectTrigger aria-label="AI Provider">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PROVIDER_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="relative w-44">
          <CalendarDays className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
          <Select value={period} onValueChange={v => setPeriod(v as Period)}>
            <SelectTrigger aria-label="Period" className="pl-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">This month</SelectItem>
              <SelectItem value="last-month">Last month</SelectItem>
              <SelectItem value="custom">Custom period</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {period === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customFrom}
              onChange={e => setCustomFrom(e.target.value)}
              aria-label="From date"
              data-testid="custom-date-from"
              className="h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
            <span className="text-sm text-muted-foreground">–</span>
            <input
              type="date"
              value={customTo}
              onChange={e => setCustomTo(e.target.value)}
              aria-label="To date"
              data-testid="custom-date-to"
              className="h-10 rounded-md border border-border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        )}

        <div className="w-44">
          <Select value={view} onValueChange={v => setView(v as View)}>
            <SelectTrigger aria-label="Type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VIEW_OPTIONS.map(option => (
                <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {view !== 'by-user' && (
        <UsageSummaryCards
          totalTokens={summary?.totalTokens}
          totalCostUsd={summary?.totalCostUsd}
          spendingCapUsd={summary?.spendingCapUsd}
        />
      )}

      {view === 'by-workflow' && (
        <TokenUsageTable
          ariaLabel="Token consumption per workflow"
          firstColumnLabel="Workflow"
          rows={workflowRows}
          isLoading={workflowQuery.isLoading}
          isError={workflowQuery.isError}
          rowTestId="workflow-row"
          emptyTestId="workflow-empty-state"
        />
      )}

      {view === 'by-user' && (
        <TokenUsageTable
          ariaLabel="Token usage by user"
          firstColumnLabel="User"
          rows={userRows}
          isLoading={userQuery.isLoading}
          isError={userQuery.isError}
          rowTestId="user-row"
          emptyTestId="user-empty-state"
        />
      )}

      {view === 'by-model' && (
        <TokenUsageTable
          ariaLabel="Token usage by model"
          firstColumnLabel="Model"
          rows={modelRows}
          isLoading={modelQuery.isLoading}
          isError={modelQuery.isError}
          rowTestId="model-row"
          emptyTestId="model-empty-state"
        />
      )}
    </div>
  )
}
